import React, {useState} from "react";
import {
    Alert,
    Box,
    Button,
    ButtonBase,
    Chip,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography
} from "@mui/material";
import {
    Add as AddIcon,
    Check as CheckIcon,
    Close as CloseIcon,
    DeleteOutline as DeleteIcon,
    EditOutlined as EditIcon,
    ExpandMore as ExpandMoreIcon,
    ChevronRight as ChevronRightIcon,
    Lock as LockIcon,
    ViewModuleOutlined as ModuloIcon
} from "@mui/icons-material";
import {ALERTAS, TIPOS_FUNCIONALIDADE, sugerirChave, validarChave} from "../catalogoRegras";
import {
    METODOS,
    bloqueiosDoModulo,
    catalogoDeManifesto,
    completude,
    contagem,
    dataDaAplicacao,
    emConstrucaoNoModulo,
    funcionalidadeCompleta,
    idDaFuncionalidade,
    idDoModulo,
    plural,
    revisaoCurta,
    validarEndpoint
} from "../sistemaPagina";
import {getBackendEntityMessage} from "../sistemasUtils";
import CampoAutoSalvo from "./CampoAutoSalvo";
import SecaoCard, {MARGEM_DA_ROLAGEM} from "./SecaoCard";
import {AlertaChip, CompletudeChip, TipoChip} from "./SistemaVisual";

const Mono = ({children, ...props}) => (
    <Typography component="span" variant="body2" sx={{fontFamily: 'monospace'}} color="text.secondary" {...props}>{children}</Typography>
);

const destaqueSx = (ativo) => (ativo ? {outline: 2, outlineColor: 'primary.main', outlineStyle: 'solid', outlineOffset: 2} : {});

const metodoDeEscrita = (funcionalidade) => (funcionalidade.recursos || []).find((recurso) => recurso.metodo !== 'GET')?.metodo;

const ChaveTravada = ({chave}) => (
    <TextField
        label="Chave"
        value={chave || ''}
        disabled
        fullWidth
        InputProps={{startAdornment: <InputAdornment position="start"><LockIcon fontSize="small" /></InputAdornment>, sx: {fontFamily: 'monospace'}}}
        helperText="Não muda depois de criada."
    />
);

const TipoToggle = ({value, onChange, rotulo}) => (
    <ToggleButtonGroup
        exclusive
        size="small"
        value={value || null}
        onChange={(_, valor) => valor && onChange(valor)}
        aria-label={rotulo}
        sx={{'& .MuiToggleButton-root': {textTransform: 'none', px: 2}}}
    >
        {TIPOS_FUNCIONALIDADE.map((tipo) => (
            <ToggleButton key={tipo.value} value={tipo.value}>{tipo.label}</ToggleButton>
        ))}
    </ToggleButtonGroup>
);

const explicacaoDoTipo = (tipo) => (tipo === 'LEITURA'
    ? 'Não altera nada: continua liberada no somente consulta.'
    : 'Cria, altera ou remove: fica bloqueada no somente consulta.');

const MetodoSelect = ({value, onChange, rotulo}) => (
    <TextField select size="small" value={value} onChange={(event) => onChange(event.target.value)} sx={{width: 120, flexShrink: 0}} inputProps={{'aria-label': rotulo}}>
        {METODOS.map((metodo) => <MenuItem key={metodo} value={metodo}>{metodo}</MenuItem>)}
    </TextField>
);

/* ---------- endpoints da funcionalidade ---------- */

const ListaDeEndpoints = ({funcionalidade, onRemover}) => {
    const recursos = funcionalidade.recursos || [];
    if (!recursos.length) {
        return <Typography variant="body2" color="text.secondary">Nenhum endpoint: a funcionalidade não libera nada a ninguém.</Typography>;
    }
    return (
        <Stack component="ul" spacing={0.75} sx={{listStyle: 'none', p: 0, m: 0}}>
            {recursos.map((recurso) => (
                <Stack component="li" key={recurso.id} direction="row" spacing={1} alignItems="center" sx={{minWidth: 0}}>
                    <Chip label={recurso.metodo} size="small" variant="outlined" sx={{fontFamily: 'monospace', minWidth: 64}} />
                    <Mono sx={{flexGrow: 1, minWidth: 0, overflowWrap: 'anywhere'}}>{recurso.endpoint}</Mono>
                    {ALERTAS[recurso.alerta_tipo_metodo] ? <AlertaChip alerta={recurso.alerta_tipo_metodo} metodo={recurso.metodo} /> : null}
                    {onRemover ? (
                        <Tooltip title="Remover endpoint">
                            <IconButton size="small" onClick={() => onRemover(recurso)} aria-label={`Remover ${recurso.metodo} ${recurso.endpoint}`}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    ) : null}
                </Stack>
            ))}
        </Stack>
    );
};

const NovoEndpoint = ({funcionalidade, onAdicionar}) => {
    const [metodo, setMetodo] = useState('GET');
    const [caminho, setCaminho] = useState('');
    const [erro, setErro] = useState('');
    const [salvando, setSalvando] = useState(false);

    const adicionar = async (event) => {
        event.preventDefault();
        const recusa = validarEndpoint(metodo, caminho, funcionalidade.recursos);
        if (recusa) {
            setErro(recusa);
            return;
        }
        setSalvando(true);
        try {
            await onAdicionar(funcionalidade, metodo, caminho.trim());
            setCaminho('');
            setErro('');
        } catch (error) {
            setErro(getBackendEntityMessage(error, 'Não foi possível adicionar o endpoint.'));
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Box component="form" onSubmit={adicionar} noValidate>
            <Stack direction={{xs: 'column', sm: 'row'}} spacing={1}>
                <MetodoSelect value={metodo} onChange={setMetodo} rotulo="Método do endpoint novo" />
                <TextField
                    size="small"
                    fullWidth
                    placeholder="/caminho/{id}"
                    value={caminho}
                    onChange={(event) => setCaminho(event.target.value)}
                    error={Boolean(erro)}
                    inputProps={{'aria-label': 'Caminho do endpoint novo', spellCheck: false, autoComplete: 'off'}}
                    InputProps={{sx: {fontFamily: 'monospace'}}}
                />
                <Button type="submit" variant="outlined" startIcon={<AddIcon />} disabled={salvando} sx={{flexShrink: 0}}>Adicionar</Button>
            </Stack>
            {erro ? <Alert severity="error" sx={{mt: 1}}>{erro}</Alert> : null}
        </Box>
    );
};

/* ---------- fonte do catálogo (BBF-103) ---------- */

const FonteCallout = ({fonte}) => {
    if (!fonte) {
        return (
            <Alert severity="info" variant="outlined" icon={<EditIcon fontSize="inherit" />}>
                <strong>Cadastro no lugar.</strong> Tudo é salvo na hora, mesmo incompleto. Os endpoints ficam dentro da funcionalidade, e o tipo leitura ou escrita é declarado nela.
            </Alert>
        );
    }
    const revisao = revisaoCurta(fonte.revisao);
    const data = dataDaAplicacao(fonte.aplicado_em);
    return (
        <Alert severity="info" variant="outlined" icon={<LockIcon fontSize="inherit" />}>
            <strong>Estrutura somente leitura.</strong> Módulos, funcionalidades, tipo e endpoints vêm do manifesto <Mono>{fonte.origem}</Mono>
            {revisao
                ? <>, na revisão <Mono>{revisao}</Mono>{data ? `, aplicada em ${data}` : ''}.</>
                : '. A revisão aplicada ainda não foi registrada: a próxima aplicação do manifesto grava.'}
            {' '}Para mudar, altere o manifesto no repositório do sistema e aplique-o. Dados e integração continuam editáveis aqui.
        </Alert>
    );
};

/* ---------- funcionalidade ---------- */

// Catálogo de manifesto: a funcionalidade aberta só mostra o que veio do manifesto.
const LeituraDaFuncionalidade = ({funcionalidade, fonte}) => {
    const alerta = ALERTAS[funcionalidade.alerta_tipo_metodo];
    return (
        <Stack spacing={2} sx={{px: {xs: 1.5, sm: 2}, pb: 2, pt: 1}}>
            {funcionalidade.descricao ? <Typography variant="body2" color="text.secondary">{funcionalidade.descricao}</Typography> : null}
            <Typography variant="caption" color="text.secondary" component="div">
                {explicacaoDoTipo(funcionalidade.tipo)} {alerta ? <strong>{alerta.explicacao}</strong> : null}
            </Typography>
            <Box>
                <Typography variant="caption" color="text.secondary" component="div" sx={{mb: 1}}>Endpoints</Typography>
                <ListaDeEndpoints funcionalidade={funcionalidade} />
            </Box>
            <Alert severity="info" variant="outlined" icon={<LockIcon fontSize="inherit" />}>
                Vem do manifesto. Para mudar o nome, o tipo ou os endpoints, altere <Mono>{fonte.origem}</Mono> no repositório do sistema.
            </Alert>
        </Stack>
    );
};

const EditorDaFuncionalidade = ({funcionalidade, modulo, modulos, acoes}) => {
    const outros = modulos.filter((item) => item.id !== modulo.id);
    const alerta = ALERTAS[funcionalidade.alerta_tipo_metodo];
    const salvar = (campos, rotulo) => acoes.salvarFuncionalidade(funcionalidade, campos, rotulo);
    return (
        <Stack spacing={2.5} sx={{px: {xs: 1.5, sm: 2}, pb: 2, pt: 1}}>
            <Box sx={{display: 'grid', gap: 2, gridTemplateColumns: {xs: '1fr', md: '1fr 1fr'}}}>
                <CampoAutoSalvo
                    label="Nome"
                    size="small"
                    value={funcionalidade.nome}
                    validar={(valor) => (valor ? '' : 'A funcionalidade precisa de um nome.')}
                    onSalvar={(valor) => salvar({nome: valor}, `nome de ${valor}`)}
                />
                <CampoAutoSalvo
                    label="Descrição"
                    size="small"
                    value={funcionalidade.descricao}
                    onSalvar={(valor) => salvar({descricao: valor}, `descrição de ${funcionalidade.nome}`)}
                />
                <Box>
                    <Typography variant="caption" color="text.secondary" component="div" sx={{mb: 0.5}}>Tipo</Typography>
                    <TipoToggle
                        value={funcionalidade.tipo}
                        rotulo={`Tipo de ${funcionalidade.nome}`}
                        onChange={(tipo) => salvar({tipo}, `tipo de ${funcionalidade.nome}`).catch(acoes.onErro)}
                    />
                    <Typography variant="caption" color="text.secondary" component="div" sx={{mt: 0.5}}>
                        {explicacaoDoTipo(funcionalidade.tipo)} {alerta ? <strong>{alerta.explicacao}</strong> : null}
                    </Typography>
                </Box>
                <ChaveTravada chave={funcionalidade.chave} />
            </Box>
            <Box>
                <Typography variant="caption" color="text.secondary" component="div" sx={{mb: 1}}>Endpoints</Typography>
                <Stack spacing={1.5}>
                    <ListaDeEndpoints funcionalidade={funcionalidade} onRemover={(recurso) => acoes.pedirRemoverEndpoint(funcionalidade, recurso)} />
                    <NovoEndpoint funcionalidade={funcionalidade} onAdicionar={acoes.adicionarEndpoint} />
                </Stack>
            </Box>
            <Stack direction={{xs: 'column', sm: 'row'}} spacing={1.5} justifyContent="space-between" alignItems={{sm: 'center'}}>
                {outros.length ? (
                    <FormControl size="small" sx={{minWidth: 240}}>
                        <InputLabel id={`mover-${funcionalidade.id}`}>Mover para</InputLabel>
                        <Select
                            labelId={`mover-${funcionalidade.id}`}
                            label="Mover para"
                            value=""
                            onChange={(event) => event.target.value && acoes.pedirMover(funcionalidade, event.target.value)}
                        >
                            {outros.map((item) => <MenuItem key={item.id} value={item.id}>{item.nome}</MenuItem>)}
                        </Select>
                    </FormControl>
                ) : <span />}
                <Button color="error" variant="outlined" size="small" startIcon={<DeleteIcon />} onClick={() => acoes.pedirRemover(funcionalidade)}>
                    Remover funcionalidade
                </Button>
            </Stack>
        </Stack>
    );
};

const FuncionalidadeItem = ({funcionalidade, modulo, modulos, aberta, destaque, fonte, acoes}) => {
    const completa = funcionalidadeCompleta(funcionalidade);
    const total = funcionalidade.recursos?.length || 0;
    return (
        <Box
            id={idDaFuncionalidade(funcionalidade.id)}
            sx={{border: 1, borderColor: 'divider', borderRadius: 1, scrollMarginTop: MARGEM_DA_ROLAGEM, bgcolor: aberta ? 'action.hover' : 'transparent', ...destaqueSx(destaque)}}
        >
            <ButtonBase
                onClick={() => acoes.alternarFuncionalidade(funcionalidade.id)}
                aria-expanded={aberta}
                sx={{width: '100%', display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', gap: 1, textAlign: 'left', p: 1.5, borderRadius: 1}}
            >
                <Box sx={{display: 'flex', pt: 0.25, color: 'text.secondary'}}>{aberta ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}</Box>
                <Box sx={{flexGrow: 1, minWidth: 0}}>
                    <Typography variant="body2" fontWeight={600}>
                        {funcionalidade.nome}
                        {!completa ? <Typography component="span" variant="caption" color="warning.main" sx={{ml: 1, fontWeight: 600}}>em construção</Typography> : null}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" component="div" sx={{overflowWrap: 'anywhere'}}>
                        <Box component="span" sx={{fontFamily: 'monospace'}}>{funcionalidade.chave}</Box>
                        {funcionalidade.descricao ? ` · ${funcionalidade.descricao}` : ''}
                    </Typography>
                </Box>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap justifyContent="flex-end" sx={{maxWidth: '50%'}}>
                    <TipoChip tipo={funcionalidade.tipo} />
                    <AlertaChip alerta={funcionalidade.alerta_tipo_metodo} metodo={metodoDeEscrita(funcionalidade)} />
                    {total
                        ? <Chip size="small" variant="outlined" label={plural(total, 'endpoint', 'endpoints')} />
                        : <Chip size="small" variant="outlined" color="warning" label="Sem endpoint" />}
                </Stack>
            </ButtonBase>
            {aberta && fonte ? <LeituraDaFuncionalidade funcionalidade={funcionalidade} fonte={fonte} /> : null}
            {aberta && !fonte ? <EditorDaFuncionalidade funcionalidade={funcionalidade} modulo={modulo} modulos={modulos} acoes={acoes} /> : null}
        </Box>
    );
};

/* ---------- formulários de criação ---------- */

const NovaFuncionalidadeForm = ({modulo, acoes, onFechar}) => {
    const [form, setForm] = useState({nome: '', chave: '', chaveManual: false, tipo: '', metodo: 'GET', caminho: ''});
    const [erro, setErro] = useState('');
    const [salvando, setSalvando] = useState(false);

    const mudar = (campo) => (event) => {
        const valor = event.target.value;
        setForm((atual) => {
            const novo = {...atual, [campo]: valor};
            if (campo === 'chave') novo.chaveManual = true;
            if (campo === 'nome' && !atual.chaveManual) novo.chave = sugerirChave(valor, modulo.chave);
            return novo;
        });
    };

    const criar = async (event) => {
        event.preventDefault();
        if (!form.nome.trim()) return setErro('Dê um nome à funcionalidade. O endpoint pode ficar para depois.');
        if (!form.tipo) return setErro('Diga se a funcionalidade é de leitura ou de escrita: o tipo é obrigatório ao criar.');
        const erroDaChave = validarChave(form.chave);
        if (erroDaChave) return setErro(erroDaChave);
        const recursos = [];
        if (form.caminho.trim()) {
            const recusa = validarEndpoint(form.metodo, form.caminho);
            if (recusa) return setErro(recusa);
            recursos.push({metodo: form.metodo, endpoint: form.caminho.trim()});
        }
        setSalvando(true);
        try {
            await acoes.criarFuncionalidade(modulo, {nome: form.nome.trim(), chave: form.chave.trim(), tipo: form.tipo, recursos});
            onFechar();
        } catch (error) {
            setErro(getBackendEntityMessage(error, 'Não foi possível criar a funcionalidade.'));
        } finally {
            setSalvando(false);
        }
        return undefined;
    };

    return (
        <Paper component="form" variant="outlined" onSubmit={criar} noValidate sx={{p: 2, borderStyle: 'dashed'}}>
            <Stack spacing={2}>
                <Typography variant="subtitle2" fontWeight={700}>Nova funcionalidade em {modulo.nome}</Typography>
                <Box sx={{display: 'grid', gap: 2, gridTemplateColumns: {xs: '1fr', md: '1fr 1fr'}}}>
                    <TextField size="small" label="Nome" value={form.nome} onChange={mudar('nome')} placeholder="Exportar relatório" autoFocus inputProps={{autoComplete: 'off'}} />
                    <TextField
                        size="small"
                        label="Chave"
                        value={form.chave}
                        onChange={mudar('chave')}
                        InputProps={{sx: {fontFamily: 'monospace'}}}
                        inputProps={{spellCheck: false, autoComplete: 'off'}}
                        helperText="Sugerida pelo nome. Não muda depois de criada."
                    />
                    <Box>
                        <Typography variant="caption" color="text.secondary" component="div" sx={{mb: 0.5}}>Tipo (obrigatório)</Typography>
                        <TipoToggle value={form.tipo} rotulo="Tipo da funcionalidade nova" onChange={(tipo) => setForm((atual) => ({...atual, tipo}))} />
                    </Box>
                    <Box>
                        <Typography variant="caption" color="text.secondary" component="div" sx={{mb: 0.5}}>Primeiro endpoint (opcional)</Typography>
                        <Stack direction="row" spacing={1}>
                            <MetodoSelect value={form.metodo} onChange={(metodo) => setForm((atual) => ({...atual, metodo}))} rotulo="Método do primeiro endpoint" />
                            <TextField
                                size="small"
                                fullWidth
                                placeholder="/caminho/{id}"
                                value={form.caminho}
                                onChange={mudar('caminho')}
                                InputProps={{sx: {fontFamily: 'monospace'}}}
                                inputProps={{'aria-label': 'Caminho do primeiro endpoint', spellCheck: false, autoComplete: 'off'}}
                            />
                        </Stack>
                    </Box>
                </Box>
                <Typography variant="caption" color="text.secondary">
                    Nome e tipo bastam para criar. Sem endpoint, a funcionalidade fica em construção, sem liberar nada, até ganhar o primeiro.
                </Typography>
                {erro ? <Alert severity="error">{erro}</Alert> : null}
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button onClick={onFechar}>Cancelar</Button>
                    <Button type="submit" variant="contained" startIcon={<CheckIcon />} disabled={salvando}>Criar funcionalidade</Button>
                </Stack>
            </Stack>
        </Paper>
    );
};

const NovoModuloForm = ({sistema, acoes, onFechar}) => {
    const [form, setForm] = useState({nome: '', chave: '', chaveManual: false, descricao: ''});
    const [erro, setErro] = useState('');
    const [salvando, setSalvando] = useState(false);

    const mudar = (campo) => (event) => {
        const valor = event.target.value;
        setForm((atual) => {
            const novo = {...atual, [campo]: valor};
            if (campo === 'chave') novo.chaveManual = true;
            if (campo === 'nome' && !atual.chaveManual) novo.chave = sugerirChave(valor, sistema.chave);
            return novo;
        });
    };

    const criar = async (event) => {
        event.preventDefault();
        if (!form.nome.trim()) return setErro('Dê um nome ao módulo.');
        const erroDaChave = validarChave(form.chave);
        if (erroDaChave) return setErro(erroDaChave);
        setSalvando(true);
        try {
            await acoes.criarModulo({nome: form.nome.trim(), chave: form.chave.trim(), descricao: form.descricao.trim()});
            onFechar();
        } catch (error) {
            setErro(getBackendEntityMessage(error, 'Não foi possível criar o módulo.'));
        } finally {
            setSalvando(false);
        }
        return undefined;
    };

    return (
        <Paper component="form" variant="outlined" onSubmit={criar} noValidate sx={{p: 2, borderStyle: 'dashed'}}>
            <Stack spacing={2}>
                <Typography variant="subtitle2" fontWeight={700}>Novo módulo em {sistema.nome}</Typography>
                <Box sx={{display: 'grid', gap: 2, gridTemplateColumns: {xs: '1fr', md: '1fr 1fr'}}}>
                    <TextField size="small" label="Nome" value={form.nome} onChange={mudar('nome')} placeholder="Relatórios" autoFocus inputProps={{autoComplete: 'off'}} />
                    <TextField
                        size="small"
                        label="Chave"
                        value={form.chave}
                        onChange={mudar('chave')}
                        InputProps={{sx: {fontFamily: 'monospace'}}}
                        inputProps={{spellCheck: false, autoComplete: 'off'}}
                        helperText="Sugerida pelo nome. Não muda depois de criada."
                    />
                    <Box sx={{gridColumn: {md: '1 / -1'}}}>
                        <TextField size="small" fullWidth label="Descrição" value={form.descricao} onChange={mudar('descricao')} inputProps={{autoComplete: 'off'}} />
                    </Box>
                </Box>
                <Typography variant="caption" color="text.secondary">
                    O módulo pertence só a este sistema. Ele nasce vazio e fica com pendência até ter uma funcionalidade completa.
                </Typography>
                {erro ? <Alert severity="error">{erro}</Alert> : null}
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button onClick={onFechar}>Cancelar</Button>
                    <Button type="submit" variant="contained" startIcon={<CheckIcon />} disabled={salvando}>Criar módulo</Button>
                </Stack>
            </Stack>
        </Paper>
    );
};

/* ---------- módulo ---------- */

const ModuloItem = ({modulo, modulos, pendencias, memoria, destaque, novaFuncionalidadeEm, fonte, acoes}) => {
    const [editando, setEditando] = useState(false);
    const aberto = Boolean(memoria.abertos[modulo.id]);
    const funcionalidades = modulo.funcionalidades || [];
    const completas = funcionalidades.filter(funcionalidadeCompleta);
    const leitura = completas.filter((item) => item.tipo === 'LEITURA').length;
    const bloqueios = bloqueiosDoModulo(pendencias, modulo.id).length;
    const construcao = emConstrucaoNoModulo(pendencias, modulo.id).length;
    const novaAberta = !fonte && novaFuncionalidadeEm === modulo.id;

    return (
        <Paper
            variant="outlined"
            id={idDoModulo(modulo.id)}
            sx={{scrollMarginTop: MARGEM_DA_ROLAGEM, borderColor: bloqueios ? 'error.main' : 'divider', ...destaqueSx(destaque === modulo.id)}}
        >
            <Stack direction="row" spacing={1} alignItems="flex-start" sx={{p: 1.5}}>
                <IconButton
                    size="small"
                    onClick={() => acoes.alternarModulo(modulo.id)}
                    aria-expanded={aberto}
                    aria-label={`${aberto ? 'Recolher' : 'Expandir'} ${modulo.nome}`}
                >
                    {aberto ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                </IconButton>
                <Box sx={{flexGrow: 1, minWidth: 0}}>
                    <Stack direction={{xs: 'column', md: 'row'}} spacing={1} alignItems={{md: 'center'}}>
                        <Box sx={{flexGrow: 1, minWidth: 0}}>
                            <Typography fontWeight={700} sx={{overflowWrap: 'anywhere'}}>
                                {modulo.nome} <Mono variant="caption">{modulo.chave}</Mono>
                            </Typography>
                            <Typography variant="body2" color="text.secondary">{modulo.descricao || 'Sem descrição'}</Typography>
                        </Box>
                        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                            <Chip size="small" variant="outlined" label={plural(funcionalidades.length, 'funcionalidade', 'funcionalidades')} />
                            {completas.length ? <Chip size="small" variant="outlined" label={`${leitura} leitura · ${completas.length - leitura} escrita`} /> : null}
                            {bloqueios ? <Chip size="small" color="error" label={plural(bloqueios, 'pendência', 'pendências')} /> : null}
                            {construcao ? <Chip size="small" color="warning" variant="outlined" label={`${construcao} em construção`} /> : null}
                        </Stack>
                    </Stack>
                </Box>
                {!editando && !fonte ? (
                    <Button size="small" startIcon={<EditIcon />} onClick={() => setEditando(true)} aria-label={`Editar o módulo ${modulo.nome}`} sx={{flexShrink: 0}}>
                        Editar
                    </Button>
                ) : null}
            </Stack>

            {editando && !fonte ? (
                <Stack spacing={2} sx={{px: 2, pb: 2}}>
                    <Box sx={{display: 'grid', gap: 2, gridTemplateColumns: {xs: '1fr', md: '1fr 1fr 1fr'}}}>
                        <CampoAutoSalvo
                            label="Nome do módulo"
                            size="small"
                            value={modulo.nome}
                            validar={(valor) => (valor ? '' : 'O módulo precisa de um nome.')}
                            onSalvar={(valor) => acoes.salvarModulo(modulo, {nome: valor}, `nome do módulo ${valor}`)}
                        />
                        <CampoAutoSalvo
                            label="Descrição"
                            size="small"
                            value={modulo.descricao}
                            onSalvar={(valor) => acoes.salvarModulo(modulo, {descricao: valor}, `descrição do módulo ${modulo.nome}`)}
                        />
                        <ChaveTravada chave={modulo.chave} />
                    </Box>
                    <Stack direction={{xs: 'column', sm: 'row'}} spacing={1} justifyContent="flex-end">
                        <Button color="error" variant="outlined" size="small" startIcon={<DeleteIcon />} onClick={() => acoes.pedirRemoverModulo(modulo)}>
                            Remover módulo
                        </Button>
                        <Button variant="outlined" size="small" startIcon={<CheckIcon />} onClick={() => setEditando(false)}>Concluir</Button>
                    </Stack>
                </Stack>
            ) : null}

            {aberto ? (
                <Stack spacing={1} sx={{px: {xs: 1.5, sm: 2}, pb: 2}}>
                    {funcionalidades.map((funcionalidade) => (
                        <FuncionalidadeItem
                            key={funcionalidade.id}
                            funcionalidade={funcionalidade}
                            modulo={modulo}
                            modulos={modulos}
                            aberta={Boolean(memoria.funcs[funcionalidade.id])}
                            destaque={destaque === funcionalidade.id}
                            fonte={fonte}
                            acoes={acoes}
                        />
                    ))}
                    {!funcionalidades.length && !novaAberta ? (
                        <Typography variant="body2" color="text.secondary" sx={{px: 1}}>
                            {fonte
                                ? 'Nenhuma funcionalidade. O módulo fica com pendência até o manifesto trazer uma completa.'
                                : 'Nenhuma funcionalidade. O módulo fica com pendência até ter uma completa.'}
                        </Typography>
                    ) : null}
                    {fonte ? null : novaAberta ? (
                        <NovaFuncionalidadeForm modulo={modulo} acoes={acoes} onFechar={() => acoes.abrirNovaFuncionalidade(null)} />
                    ) : (
                        <Box>
                            <Button size="small" startIcon={<AddIcon />} onClick={() => acoes.abrirNovaFuncionalidade(modulo.id)}>
                                Nova funcionalidade em {modulo.nome}
                            </Button>
                        </Box>
                    )}
                </Stack>
            ) : null}
        </Paper>
    );
};

/* ---------- seção ---------- */

export default function CatalogoSecao({sistema, modulos, pendencias, memoria, destaque, novoModuloAberto, novaFuncionalidadeEm, acoes}) {
    // Catálogo de manifesto (BBF-103): sem Novo módulo, sem edição e sem remover; a API recusaria.
    const fonte = catalogoDeManifesto(sistema) ? sistema.fonte_catalogo : null;
    const c = contagem(modulos);
    const todosAbertos = modulos.length > 0 && modulos.every((modulo) => memoria.abertos[modulo.id]);
    const resumo = [
        plural(modulos.length, 'módulo', 'módulos'),
        `${plural(c.funcionalidades, 'funcionalidade', 'funcionalidades')}${c.completas ? ` (${c.leitura} de leitura, ${c.escrita} de escrita${c.funcionalidades > c.completas ? `, ${c.funcionalidades - c.completas} em construção` : ''})` : ''}`,
        plural(c.endpoints, 'endpoint', 'endpoints'),
    ].join(' · ');

    return (
        <SecaoCard
            id="catalogo"
            titulo="Módulos e funcionalidades"
            subtitulo={resumo}
            semCartao
            chip={<CompletudeChip completude={completude('catalogo', {sistema, pendencias})} />}
            acoes={(
                <>
                    {modulos.length ? (
                        <Button size="small" variant="outlined" onClick={() => acoes.todos(!todosAbertos)}>
                            {todosAbertos ? 'Recolher tudo' : 'Expandir tudo'}
                        </Button>
                    ) : null}
                    {!fonte ? (
                        <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => acoes.abrirNovoModulo(true)}>Novo módulo</Button>
                    ) : null}
                </>
            )}
        >
            <Stack spacing={1.5}>
                <FonteCallout fonte={fonte} />
                {modulos.map((modulo) => (
                    <ModuloItem
                        key={modulo.id}
                        modulo={modulo}
                        modulos={modulos}
                        pendencias={pendencias}
                        memoria={memoria}
                        destaque={destaque}
                        novaFuncionalidadeEm={novaFuncionalidadeEm}
                        fonte={fonte}
                        acoes={acoes}
                    />
                ))}
                {!modulos.length && (fonte || !novoModuloAberto) ? (
                    <Paper variant="outlined" sx={{p: 3, textAlign: 'center', borderStyle: 'dashed'}}>
                        <ModuloIcon color="disabled" />
                        <Typography color="text.secondary" sx={{mb: fonte ? 0 : 1.5}}>
                            {fonte
                                ? 'Nenhum módulo ainda. Os módulos chegam quando o manifesto for aplicado.'
                                : 'Nenhum módulo ainda. O sistema fica com pendência até ter um módulo com uma funcionalidade completa.'}
                        </Typography>
                        {!fonte ? (
                            <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => acoes.abrirNovoModulo(true)}>Criar o primeiro módulo</Button>
                        ) : null}
                    </Paper>
                ) : null}
                {novoModuloAberto && !fonte ? <NovoModuloForm sistema={sistema} acoes={acoes} onFechar={() => acoes.abrirNovoModulo(false)} /> : null}
            </Stack>
        </SecaoCard>
    );
}
